import { useEffect, useState, useCallback } from "react";
import { CreditCard, Calendar, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCorporate } from "./CorporateLayout";
import { DowngradeCard } from "@/components/corporate/DowngradeCard";
import { SubscriptionManager } from "@/components/billing/SubscriptionManager";

export default function CorporateBilling() {
  const { corp } = useCorporate();
  const [billing, setBilling] = useState(null);
  const [plan, setPlan] = useState("monthly");
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [locCount, setLocCount] = useState(0);

  const load = useCallback(() => {
    api.get("/billing/status").then(({ data }) => setBilling(data)).catch(() => {});
    api.get("/corporate/dashboard").then(({ data }) => setLocCount(data?.totals?.locations || 0)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const startCheckout = async () => {
    setCheckoutBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
      setCheckoutBusy(false);
    }
  };

  const monthly = billing?.monthly_price_usd ?? 0;
  const annual = billing?.annual_price_usd ?? 0;
  const savings = Math.max(0, monthly * 12 - annual);
  const subActive = !!billing?.subscription?.active;

  return (
    <main className="px-6 md:px-10 py-8 max-w-4xl" data-testid="corporate-billing-section">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Billing</h1>
        <p className="text-sm text-slate-600">One bill for the whole {corp?.name || "district"} group.</p>
      </header>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm" data-testid="corporate-billing-card">
        <div className="font-display text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" /> Subscription
        </div>
        <div className="text-sm text-slate-600">Billing email: <span className="font-semibold">{corp?.billing_email}</span></div>
        <div className="mt-3 flex items-center gap-3 text-sm text-slate-700">
          <Calendar className="h-4 w-4 text-slate-400" />
          {billing?.status === "active"
            ? <>{subActive && !billing.subscription.cancel_at_period_end ? "Auto-renews" : subActive ? "Access until" : "Renews"} <span className="font-semibold">{new Date(billing.paid_until).toLocaleDateString()}</span> · <span className="text-emerald-600 font-semibold">{billing.days_left} days left</span></>
            : billing?.status === "trial"
            ? <>On trial until <span className="font-semibold">{new Date(billing.trial_ends_at).toLocaleDateString()}</span> · <span className="text-amber-600 font-semibold">{billing.days_left} days left</span></>
            : <span className="text-rose-700 font-semibold">Subscription expired — renew below to keep all locations active.</span>}
        </div>
        <div className="mt-1 text-xs text-slate-500">One bill covers all {locCount} linked location{locCount === 1 ? "" : "s"}.</div>
        {billing?.quote && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="corporate-quote">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Plan size</div>
              <div className="inline-flex items-center gap-2" title="Changes automatically when you add, archive or delete a location">
                <span className="text-xs text-slate-500">Locations on plan</span>
                <span data-testid="corporate-plan-locations" className="inline-flex h-8 min-w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm font-semibold text-slate-500">{billing.quote.location_count}</span>
              </div>
            </div>
            {(billing.quote.lines || [{ type: "other", count: billing.quote.location_count, unit_price_usd: billing.quote.price_per_location_usd }]).map((l, i) => (
              <div key={i} className="flex items-baseline justify-between" data-testid={`corporate-quote-line-${l.type}`}>
                <span className="text-slate-600">{l.count} {({ school: "school", daycare: "daycare", care: "care home", business: "office", other: "location" })[l.type] || l.type}{l.count === 1 ? "" : "s"} × ${l.unit_price_usd.toFixed(2)}</span>
                <span className="font-semibold">${(l.count * l.unit_price_usd).toFixed(2)}</span>
              </div>
            ))}
            {billing.quote.free_locations > 0 && <div className="flex items-baseline justify-between text-emerald-700" data-testid="corporate-quote-free"><span>Volume discount · {billing.quote.free_locations} free</span><span className="font-semibold">−${(billing.quote.discount_usd ?? billing.quote.free_locations * billing.quote.price_per_location_usd).toFixed(2)}</span></div>}
            <div className="mt-2 pt-2 border-t border-slate-200 flex items-baseline justify-between"><span className="font-semibold">Monthly total</span><span className="font-display text-lg font-bold" data-testid="corporate-quote-total">${billing.quote.monthly_total_usd.toFixed(2)}</span></div>
            {(() => { const next = billing.quote.free_tiers.find((t) => t.min_locations > billing.quote.location_count); return next ? <div className="mt-1 text-[11px] text-slate-500">Reach {next.min_locations} locations and {next.free_locations} are free.</div> : null; })()}
            <div className="mt-1 text-[11px] text-slate-500">Plan size updates automatically when you add, archive or delete a location.</div>
          </div>
        )}

        {subActive ? (
          <div className="mt-6"><SubscriptionManager billing={billing} onChanged={load} /></div>
        ) : (<>
        <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="corporate-plan-selector">
          <button
            type="button"
            onClick={() => setPlan("monthly")}
            data-testid="corporate-plan-monthly"
            className={`rounded-xl border-2 p-4 text-left transition ${
              plan === "monthly" ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Monthly</div>
            <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
              ${monthly.toFixed(2)}
              <span className="text-sm font-normal text-slate-500"> / 30 days</span>
            </div>
            {corp?.custom_monthly_price_usd > 0 && (
              <div className="text-xs text-slate-500 mt-1">Custom enterprise plan · unlimited locations</div>
            )}
          </button>
          <button
            type="button"
            onClick={() => setPlan("annual")}
            data-testid="corporate-plan-annual"
            className={`relative rounded-xl border-2 p-4 text-left transition ${
              plan === "annual" ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            {savings > 0 && (
              <span className="absolute -top-2.5 right-3 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                Save ${savings.toFixed(0)}
              </span>
            )}
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Annual</div>
            <div className="font-display text-3xl font-bold tracking-tight text-slate-900">
              ${annual.toFixed(2)}
              <span className="text-sm font-normal text-slate-500"> / 365 days</span>
            </div>
          </button>
        </div>

        <button onClick={startCheckout} disabled={checkoutBusy} data-testid="corporate-checkout-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-semibold text-white hover:shadow disabled:opacity-60 transition">
          {checkoutBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          Subscribe · auto-renews {plan === "annual" ? "yearly" : "monthly"}
        </button>
        <div className="mt-3 text-xs text-slate-500">Secure payment by Stripe. Your card is charged automatically each cycle until you cancel{billing?.status === "active" ? " — the first charge happens when your current paid period ends" : ""}.</div>
        </>)}
      </section>

      <DowngradeCard corp={corp} />
    </main>
  );
}
